import React, { useState, useEffect } from 'react';
import {
  Card,
  Calendar,
  Badge,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Tag,
  Typography,
  Row,
  Col,
  message,
  Tooltip,
  Avatar,
  List,
  Divider,
  Switch,
  Alert,
  Popover
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  EnvironmentOutlined,
  BellOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  TeamOutlined,
  MailOutlined,
  MessageOutlined
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Appointment {
  id: string;
  title: string;
  description: string;
  type: 'meeting' | 'call' | 'demo' | 'consultation' | 'follow-up' | 'presentation';
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no-show';
  startTime: string;
  endTime: string;
  duration: number;
  location?: string;
  meetingType: 'in-person' | 'phone' | 'video' | 'virtual';
  organizer: string;
  attendees: Attendee[];
  customer: string;
  contactPerson: string;
  email: string;
  phone: string;
  priority: 'low' | 'medium' | 'high';
  reminderSent: boolean;
  notes: string;
  outcome?: string;
  followUpRequired: boolean;
  followUpDate?: string;
  createdDate: string;
  lastModified: string;
}

interface Attendee {
  id: string;
  name: string;
  email: string;
  role: string;
  status: 'invited' | 'accepted' | 'declined' | 'tentative';
}

const AppointmentScheduling: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [loading, setLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [form] = Form.useForm();

  // Mock data
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const mockAppointments: Appointment[] = [
        {
          id: '1',
          title: 'Product Demo - TechCorp Solutions',
          description: 'Demonstrate ERP features and capabilities to technical team',
          type: 'demo',
          status: 'confirmed',
          startTime: '2024-06-26T10:00:00',
          endTime: '2024-06-26T11:30:00',
          duration: 90,
          location: 'Conference Room A',
          meetingType: 'in-person',
          organizer: 'Sarah Johnson',
          attendees: [
            {
              id: 'att1',
              name: 'John Smith',
              email: 'john.smith@techcorp.com',
              role: 'CTO',
              status: 'accepted'
            },
            {
              id: 'att2',
              name: 'Jane Doe',
              email: 'jane.doe@techcorp.com',
              role: 'IT Manager',
              status: 'accepted'
            }
          ],
          customer: 'TechCorp Solutions',
          contactPerson: 'John Smith',
          email: 'john.smith@techcorp.com',
          phone: '+1-555-0123',
          priority: 'high',
          reminderSent: true,
          notes: 'Focus on integration capabilities and scalability',
          followUpRequired: true,
          followUpDate: '2024-06-28',
          createdDate: '2024-06-20',
          lastModified: '2024-06-24'
        },
        {
          id: '2',
          title: 'Discovery Call - Startup Innovations',
          description: 'Initial requirements gathering and needs assessment',
          type: 'call',
          status: 'scheduled',
          startTime: '2024-06-27T14:00:00',
          endTime: '2024-06-27T15:00:00',
          duration: 60,
          meetingType: 'phone',
          organizer: 'Mike Chen',
          attendees: [
            {
              id: 'att3',
              name: 'Emily Davis',
              email: 'emily.davis@startup.io',
              role: 'Founder',
              status: 'tentative'
            }
          ],
          customer: 'Startup Innovations',
          contactPerson: 'Emily Davis',
          email: 'emily.davis@startup.io',
          phone: '+1-555-0124',
          priority: 'medium',
          reminderSent: false,
          notes: 'New startup, budget conscious, looking for quick implementation',
          followUpRequired: true,
          createdDate: '2024-06-24',
          lastModified: '2024-06-24'
        },
        {
          id: '3',
          title: 'Proposal Presentation - Enterprise Corp',
          description: 'Final proposal presentation to executive committee',
          type: 'presentation',
          status: 'confirmed',
          startTime: '2024-06-28T09:00:00',
          endTime: '2024-06-28T10:30:00',
          duration: 90,
          location: 'Enterprise Corp Headquarters',
          meetingType: 'in-person',
          organizer: 'Alex Thompson',
          attendees: [
            {
              id: 'att4',
              name: 'Michael Rodriguez',
              email: 'michael@enterprise.com',
              role: 'VP Sales',
              status: 'accepted'
            },
            {
              id: 'att5',
              name: 'Robert Johnson',
              email: 'robert@enterprise.com',
              role: 'CEO',
              status: 'accepted'
            }
          ],
          customer: 'Enterprise Corp',
          contactPerson: 'Michael Rodriguez',
          email: 'michael@enterprise.com',
          phone: '+1-555-0125',
          priority: 'high',
          reminderSent: true,
          notes: 'Final decision meeting, prepare for pricing discussions',
          followUpRequired: true,
          followUpDate: '2024-06-30',
          createdDate: '2024-06-15',
          lastModified: '2024-06-25'
        }
      ];
      setAppointments(mockAppointments);
      setLoading(false);
    }, 1000);
  }, []);

  const getAppointmentsByDate = (date: Dayjs) => {
    return appointments.filter(appointment => 
      dayjs(appointment.startTime).isSame(date, 'day')
    );
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'scheduled': 'blue',
      'confirmed': 'green',
      'completed': 'success',
      'cancelled': 'red',
      'no-show': 'orange'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      'meeting': <TeamOutlined />,
      'call': <PhoneOutlined />,
      'demo': <VideoCameraOutlined />,
      'consultation': <UserOutlined />,
      'follow-up': <SyncOutlined />,
      'presentation': <CalendarOutlined />
    };
    return icons[type as keyof typeof icons] || <CalendarOutlined />;
  };

  const getMeetingTypeIcon = (meetingType: string) => {
    const icons = {
      'in-person': <EnvironmentOutlined />,
      'phone': <PhoneOutlined />,
      'video': <VideoCameraOutlined />,
      'virtual': <VideoCameraOutlined />
    };
    return icons[meetingType as keyof typeof icons] || <CalendarOutlined />;
  };

  const handleAddAppointment = () => {
    setSelectedAppointment(null);
    form.resetFields();
    form.setFieldsValue({
      startTime: selectedDate,
      duration: 60,
      meetingType: 'video',
      priority: 'medium',
      type: 'meeting'
    });
    setModalVisible(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    form.setFieldsValue({
      ...appointment,
      startTime: dayjs(appointment.startTime),
      followUpDate: appointment.followUpDate ? dayjs(appointment.followUpDate) : null,
    });
    setModalVisible(true);
  };

  const handleSaveAppointment = async (values: any) => {
    try {
      const startTime = values.startTime;
      const endTime = startTime.add(values.duration, 'minute');
      
      const appointmentData = {
        ...values,
        id: selectedAppointment?.id || Date.now().toString(),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        followUpDate: values.followUpDate ? values.followUpDate.format('YYYY-MM-DD') : null,
        lastModified: dayjs().toISOString(),
        attendees: selectedAppointment?.attendees || [],
        reminderSent: false
      };

      if (selectedAppointment) {
        setAppointments(appointments.map(appointment => 
          appointment.id === selectedAppointment.id ? appointmentData : appointment
        ));
        message.success('Appointment updated successfully');
      } else {
        setAppointments([...appointments, appointmentData]);
        message.success('Appointment scheduled successfully');
      }

      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Failed to save appointment');
    }
  };

  const handleStatusChange = (appointmentId: string, newStatus: string) => {
    setAppointments(appointments.map(appointment => 
      appointment.id === appointmentId 
        ? { ...appointment, status: newStatus as any }
        : appointment
    ));
    message.success(`Appointment marked as ${newStatus}`);
  };

  const dateCellRender = (value: Dayjs) => {
    const dayAppointments = getAppointmentsByDate(value);
    return (
      <div>
        {dayAppointments.map(appointment => (
          <div key={appointment.id} style={{ marginBottom: 2 }}>
            <Tooltip title={`${appointment.title} - ${dayjs(appointment.startTime).format('HH:mm')}`}>
              <Badge 
                status={getStatusColor(appointment.status) as any} 
                text={
                  <span style={{ fontSize: '11px', cursor: 'pointer' }} onClick={() => handleEditAppointment(appointment)}>
                    {appointment.title.substring(0, 20)}...
                  </span>
                }
              />
            </Tooltip>
          </div>
        ))}
      </div>
    );
  };

  const todaysAppointments = getAppointmentsByDate(dayjs());
  const upcomingAppointments = appointments
    .filter(appointment => dayjs(appointment.startTime).isAfter(dayjs()))
    .sort((a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix())
    .slice(0, 5);

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <CalendarOutlined style={{ marginRight: 8 }} />
            Appointment Scheduling
          </Title>
        </Col>
        <Col>
          <Space>
            <Button 
              type={viewMode === 'calendar' ? 'primary' : 'default'}
              onClick={() => setViewMode('calendar')}
              icon={<CalendarOutlined />}
            >
              Calendar
            </Button>
            <Button 
              type={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
              icon={<TeamOutlined />}
            >
              List
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAppointment}>
              Schedule Appointment
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Today's Appointments Alert */}
      {todaysAppointments.length > 0 && (
        <Alert
          message={`You have ${todaysAppointments.length} appointment(s) scheduled for today`}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          action={
            <Button size="small" type="text">
              View All
            </Button>
          }
        />
      )}

      <Row gutter={24}>
        <Col xs={24} lg={18}>
          {viewMode === 'calendar' ? (
            <Card>
              <Calendar
                dateCellRender={dateCellRender}
                onSelect={(date) => setSelectedDate(date)}
                value={selectedDate}
              />
            </Card>
          ) : (
            <Card title="All Appointments">
              <List
                dataSource={appointments.sort((a, b) => dayjs(a.startTime).unix() - dayjs(b.startTime).unix())}
                renderItem={(appointment) => (
                  <List.Item
                    actions={[
                      <Button 
                        type="link" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEditAppointment(appointment)}
                      />,
                      <Button 
                        type="link" 
                        icon={<PhoneOutlined />} 
                      />,
                      <Button 
                        type="link" 
                        icon={<MailOutlined />} 
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar icon={getTypeIcon(appointment.type)} />
                      }
                      title={
                        <div>
                          <Space>
                            {appointment.title}
                            <Tag color={getStatusColor(appointment.status)}>
                              {appointment.status.toUpperCase()}
                            </Tag>
                            {appointment.priority === 'high' && (
                              <Tag color="red">HIGH PRIORITY</Tag>
                            )}
                          </Space>
                        </div>
                      }
                      description={
                        <div>
                          <div>
                            <Space>
                              <Text>{dayjs(appointment.startTime).format('MMM DD, YYYY HH:mm')}</Text>
                              <Text>-</Text>
                              <Text>{dayjs(appointment.endTime).format('HH:mm')}</Text>
                              {getMeetingTypeIcon(appointment.meetingType)}
                              <Text>{appointment.customer}</Text>
                            </Space>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Text type="secondary">{appointment.description}</Text>
                          </div>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </Col>

        <Col xs={24} lg={6}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Today's Schedule */}
            <Card title="Today's Schedule" size="small">
              {todaysAppointments.length > 0 ? (
                <List
                  size="small"
                  dataSource={todaysAppointments}
                  renderItem={(appointment) => (
                    <List.Item>
                      <div style={{ width: '100%' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                          {dayjs(appointment.startTime).format('HH:mm')} - {appointment.title.substring(0, 25)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          {appointment.customer}
                        </div>
                        <Tag size="small" color={getStatusColor(appointment.status)}>
                          {appointment.status}
                        </Tag>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary">No appointments scheduled for today</Text>
              )}
            </Card>

            {/* Upcoming Appointments */}
            <Card title="Upcoming Appointments" size="small">
              {upcomingAppointments.length > 0 ? (
                <List
                  size="small"
                  dataSource={upcomingAppointments}
                  renderItem={(appointment) => (
                    <List.Item>
                      <div style={{ width: '100%' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                          {dayjs(appointment.startTime).format('MMM DD HH:mm')} - {appointment.title.substring(0, 20)}
                        </div>
                        <div style={{ fontSize: '11px', color: '#666' }}>
                          {appointment.customer}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Text type="secondary">No upcoming appointments</Text>
              )}
            </Card>

            {/* Quick Actions */}
            <Card title="Quick Actions" size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button block icon={<CalendarOutlined />}>View This Week</Button>
                <Button block icon={<BellOutlined />}>Send Reminders</Button>
                <Button block icon={<SyncOutlined />}>Sync Calendar</Button>
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>

      {/* Add/Edit Appointment Modal */}
      <Modal
        title={selectedAppointment ? 'Edit Appointment' : 'Schedule New Appointment'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width={800}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveAppointment}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Appointment Title"
                rules={[{ required: true, message: 'Please enter appointment title' }]}
              >
                <Input placeholder="e.g., Product Demo - TechCorp Solutions" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customer"
                label="Customer/Company"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactPerson"
                label="Contact Person"
                rules={[{ required: true, message: 'Please enter contact person' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input prefix={<PhoneOutlined />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="type"
                label="Appointment Type"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select>
                  <Option value="meeting">Meeting</Option>
                  <Option value="call">Call</Option>
                  <Option value="demo">Demo</Option>
                  <Option value="consultation">Consultation</Option>
                  <Option value="follow-up">Follow-up</Option>
                  <Option value="presentation">Presentation</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="meetingType"
                label="Meeting Type"
                rules={[{ required: true, message: 'Please select meeting type' }]}
              >
                <Select>
                  <Option value="in-person">In-Person</Option>
                  <Option value="phone">Phone</Option>
                  <Option value="video">Video Conference</Option>
                  <Option value="virtual">Virtual Meeting</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                rules={[{ required: true, message: 'Please select priority' }]}
              >
                <Select>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="startTime"
                label="Date & Time"
                rules={[{ required: true, message: 'Please select date and time' }]}
              >
                <DatePicker 
                  showTime 
                  style={{ width: '100%' }} 
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="duration"
                label="Duration (minutes)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={15}
                  step={15}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="organizer"
                label="Organizer"
                rules={[{ required: true, message: 'Please select organizer' }]}
              >
                <Select>
                  <Option value="Sarah Johnson">Sarah Johnson</Option>
                  <Option value="Mike Chen">Mike Chen</Option>
                  <Option value="Alex Thompson">Alex Thompson</Option>
                  <Option value="Lisa Wang">Lisa Wang</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="location" label="Location">
                <Input placeholder="Conference Room A / Zoom Link / Address" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Appointment description and agenda..." />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes..." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="followUpRequired" valuePropName="checked">
                <Switch checkedChildren="Follow-up Required" unCheckedChildren="No Follow-up" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="followUpDate" label="Follow-up Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedAppointment ? 'Update Appointment' : 'Schedule Appointment'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              {selectedAppointment && (
                <>
                  <Button icon={<MailOutlined />}>Send Reminder</Button>
                  <Button icon={<MessageOutlined />}>Send SMS</Button>
                </>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AppointmentScheduling;
